import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map, Subscription } from 'rxjs';
import {
  Filters,
  FnCaricamentoDati,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import {
  BottoniListaEvent,
  Colonna,
  Filtri,
  ListaTabellareComponent,
} from 'src/app/components/lista-tabellare/lista-tabellare.component';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { Azienda, AziendaService } from 'src/app/services/azienda/azienda.service';
import { LicenzeService } from 'src/app/services/licenze/licenze.service';
import { MaterialitaService } from 'src/app/services/materialita/materialita.service';
import { SettoriService } from 'src/app/services/settori/settori.service';
import { SintesiService } from 'src/app/services/sintesi/sintesi.service';
import { UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogCreaSintesiSostenibilitaComponent } from './dialog/dialog-crea-sintesi-sostenibilita/dialog-crea-sintesi-sostenibilita.component';

@Component({
  selector: 'app-sintesi-sostenibilita',
  templateUrl: './sintesi-sostenibilita.component.html',
  styleUrls: ['./sintesi-sostenibilita.component.scss'],
  standalone: false,
})
export class SintesiSostenibilitaComponent {
  @ViewChild('tabella') tabella!: ListaTabellareComponent;
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;
  private azienda: Azienda | undefined = undefined;

  public filtri: Filtri[] = [
    {
      titolo: 'Anno',
      forControlName: 'anno',
      input: 'text',
    },
    {
      forControlName: 'settori',
      input: 'multiple-option',
      titolo: 'Settore',
      fnDatiOption: () =>
        this.settoriService.getSettori(0, 1000).pipe(map((value) => value?.content || [])),
      descValueOption: 'titolo',
      idValueOption: 'id',
      riempi: true,
      larghezza: 1,
    },
  ];

  public colonne: { [key: string]: Colonna } = {
    anno: {
      title: 'Anno',
      value: 'anno',
      sortable: true,
    },
    settore: {
      title: 'Settore',
      sortable: true,
      campiSort: ['settore'],
      value: (record: any) => record?.settori[0]?.titolo || '',
    },
    risultatiImpegni: {
      title: 'Impegni',
      value: (record: any) =>
        (record?.compilazioni?.impegni?.compilati || 0) +
        '/' +
        (record?.compilazioni?.impegni?.totali || 0),
    },
    risultatiAzioni: {
      title: 'Azioni',
      value: (record: any) =>
        (record?.compilazioni?.azioni?.compilati || 0) +
        '/' +
        (record?.compilazioni?.azioni?.totali || 0),
    },
    risultatiKpi: {
      title: 'Kpi',
      value: (record: any) =>
        (record?.compilazioni?.kpi?.compilati || 0) +
        '/' +
        (record?.compilazioni?.kpi?.totali || 0),
    },
    dataUltimaModifica: {
      title: 'Ultima Modifica',
      value: (record: any) => record?.dataUltimaModifica ?? '',
    },
    dataCreazione: {
      title: 'Data Creazione',
      value: (record: any) => record?.dataCreazione ?? '',
    },

    stato: this.utilityService.getColonnaStato(),

    modifica: {
      type: 'button',
      buttonIcon: (record: any) => (this.isPermessoSoloRead(record) ? 'visibility' : 'edit'),
      title: (record: any) => (this.isPermessoSoloRead(record) ? 'Visualizza' : 'Modifica'),
      buttonId: 'modifica',
      buttonMostraSempre: true,
      sortable: false,
    },
    elimina: {
      ...this.utilityService.getColonnaElimina(),
      nascondiButton: this.nascondiBottoneElimina(),
    },
    elimina_by_developer: {
      type: 'button',
      buttonIcon: 'delete',
      title: 'Elimina by Developer',
      buttonId: 'elimina_by_developer',
      buttonMostraSempre: false,
      sortable: false,
      nascondiButton: (record: any) => !this.isDeveloper || record.stato === 'BOZZA',
    },
  };

  get isDeveloper() {
    return !!this.utenteService.infoUtente?.isDeveloper;
  }

  private _subAziendaCorrente: Subscription | undefined;

  constructor(
    public dialog: MatDialog,
    private readonly utilityService: UtilityService,
    private readonly materialitaService: MaterialitaService,
    private readonly aziendaService: AziendaService,
    private readonly sintesiService: SintesiService,
    public utenteService: UtenteService,
    private readonly licenzeService: LicenzeService,
    private readonly settoriService: SettoriService,
  ) {}

  nascondiBottoneElimina() {
    if (this.utenteService.infoUtente?.isDeveloper) {
      return false; //non nascondere
    }
    if (this.utenteService.isPermessoAttivo('DELETE_SINTESI')) {
      return false; //nascondere
    }
    return true;
  }

  isPermessoSoloRead(record: any) {
    const isEdit = this.utenteService.isPermessoAttivo('EDIT_SINTESI');
    const isCreate = this.utenteService.isPermessoAttivo('CREATE_SINTESI');
    const statoArchiviato = record.stato === 'ARCHIVIATO';
    return (!isEdit && !isCreate) || statoArchiviato;
  }

  ngAfterViewInit(): void {
    this._subAziendaCorrente = this.aziendaService.aziendaCorrente.subscribe((azienda) => {
      let cambiato = false;

      if (this.azienda && azienda && this.azienda.id !== azienda.id) {
        cambiato = true;
      }

      this.azienda = azienda;

      if (cambiato) {
        this.tabella?.caricaDati();
      }
    });
  }

  ngOnDestroy(): void {
    this._subAziendaCorrente?.unsubscribe();
  }

  public fnCaricamentoDati: FnCaricamentoDati = (
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) => {
    return this.sintesiService.getSintesi(page, pageSize, ricerca, filters, sortBy);
  };

  /**
   * Metodo di modifica o creazione nuova sintesi
   * @param record
   */
  crea(record?: any) {
    if (!record) {
      this.spinnerOver.show();
      this.licenzeService.getUserHasLicense('Sintesi').subscribe({
        next: (esito) => {
          this.apriDialogCreaSintesi(record);
          this.spinnerOver.hide();
        },
        error: (err) => {
          this.spinnerOver.hide();
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: err?.error?.message,
            bottoni: [{ nome_btn: 'Ok' }],
          });
        },
      });
    } else {
      this.apriDialogCreaSintesi(record);
    }
  }

  private apriDialogCreaSintesi(record: any) {
    const dialogCreaSintesi = this.dialog.open(DialogCreaSintesiSostenibilitaComponent, {
      data: {
        sintesi: record,
      },
      panelClass: ['dialog-container-scrollbar'],
      disableClose: false,
      width: '80%',
      height: '99%',
      autoFocus: false,
    });

    dialogCreaSintesi.afterClosed().subscribe((result) => {
      this.tabella.caricaDati();
    });
  }

  public bottoniListaClick(event: BottoniListaEvent) {
    switch (event.id) {
      case 'modifica':
        this.crea(event.data);
        break;
      case 'elimina':
      case 'archivia':
      case 'disarchivia':
        this._elimina(event.data, false);
        break;

      case 'elimina_by_developer':
        this._elimina(event.data, true);
        break;
      default:
        console.warn('ATTENZIONE: bottone ' + event.id + ' non riconosciuto');
    }
  }

  /**
   * Gestisce l'eliminazione di un modulo tramite il service `utilityService`.
   * Mostra lo spinner durante l'operazione e aggiorna la tabella al termine.
   *
   * @async
   * @param {any} modulo - L'oggetto modulo da eliminare. Deve contenere almeno l'ID e lo stato.
   *
   */
  async _elimina(modulo: any, deleteByDeveloper: boolean) {
    this.spinnerOver.show();

    const success = await this.utilityService.eliminaModulo(
      modulo,
      this.sintesiService,
      deleteByDeveloper,
    );

    this.spinnerOver.hide();

    if (success) {
      this.tabella.caricaDati();
    } else {
      console.log('Operazione annullata o non riuscita');
    }
  }
}
